import {procurementTenantCgaId} from './procurementTenantContext'

type IdLike = number | string | {id?: unknown} | null | undefined

export type BuyerCompanyGroupAdminContext = {
  requisition?: Record<string, unknown> | null
  rfq?: Record<string, unknown> | null
  quotation?: Record<string, unknown> | null
  currentUser?: unknown
}

function positiveId(value: unknown): number | undefined {
  if (value == null) return undefined
  if (typeof value === 'object') {
    const record = value as {id?: unknown}
    if ('id' in record) {
      return positiveId(record.id)
    }
    return undefined
  }
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

function requisitionLineVesselCgaId(requisition?: Record<string, unknown> | null): number | undefined {
  const lines = requisition?.lines
  if (!Array.isArray(lines) || lines.length === 0) return undefined
  for (const line of lines) {
    const part = (line as {part?: {vessel?: Record<string, unknown>}})?.part
    const vessel = part?.vessel
    const fromVessel = positiveId(vessel?.companyGroupAdminId)
    if (fromVessel) return fromVessel
  }
  return undefined
}

function legacyRequisitionCgaId(requisition?: Record<string, unknown> | null): number | undefined {
  return positiveId(requisition?.companyGroupAdminId)
}

/**
 * Resolve buyer company-group admin ID from procurement business data.
 * Never uses user.id, role.id, uid, roleEntityId, or hardcoded fallbacks.
 */
export function resolveBuyerCompanyGroupAdminId(
  context: BuyerCompanyGroupAdminContext
): number | undefined {
  const {requisition, rfq, quotation, currentUser} = context

  const candidates: unknown[] = [
    (requisition?.category as {cgaid?: IdLike} | undefined)?.cgaid,
    requisition?.cgaid,
    (requisition?.cgaid as {id?: unknown} | undefined)?.id,
    rfq?.cgaid,
    (rfq?.cgaid as {id?: unknown} | undefined)?.id,
    rfq?.companyGroupAdminId,
    quotation?.cgaid,
    (quotation?.cgaid as {id?: unknown} | undefined)?.id,
    quotation?.companyGroupAdminId,
    legacyRequisitionCgaId(requisition),
    requisitionLineVesselCgaId(requisition),
    procurementTenantCgaId(currentUser),
  ]

  for (const candidate of candidates) {
    const resolved = positiveId(candidate)
    if (resolved) return resolved
  }

  return undefined
}

export function normalizeProcurementListResponse<T>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[]
  if (value && typeof value === 'object' && Array.isArray((value as {content?: unknown}).content)) {
    return (value as {content: T[]}).content
  }
  return []
}
